// =========================================================
// ClientsManager.jsx
// ORION BEACH HOTEL · KITE HOUSE
// GESTIÓN PREMIUM DE HUÉSPEDES / CONTACTOS
// =========================================================

import { useEffect, useMemo, useState } from "react";
import "../styles/clients.css";

// =========================================================
// CONFIGURACIÓN DE API
// =========================================================
//
// VITE_API_URL
//
// Desarrollo:
// VITE_API_URL=http://127.0.0.1:8000
//
// Producción:
// VITE_API_URL=https://hotelorionmayapo.com
//
// IMPORTANTE:
// No colocar /api al final de VITE_API_URL.
// El componente agrega /api automáticamente.
//
// Ejemplo desarrollo:
// http://127.0.0.1:8000
//
// Ejemplo producción:
// https://hotelorionmayapo.com
//
// =========================================================

const DEFAULT_API_URL =
  import.meta.env.DEV
    ? "http://127.0.0.1:8000"
    : "https://hotelorionmayapo.com";

const API = (
  import.meta.env.VITE_API_URL ||
  DEFAULT_API_URL
).replace(/\/+$/, "");

// =========================================================
// API BASE
// =========================================================

const API_BASE = `${API}/api`;

// =========================================================
// ENDPOINT DE CLIENTES
// =========================================================

const CLIENTS_ENDPOINT = `${API_BASE}/clients/`;

// =========================================================
// INITIAL FORM
// =========================================================

const INITIAL_FORM = {
  name: "",
  phone: "",
  message: "",
};

// =========================================================
// HELPERS
// =========================================================

/**
 * Normaliza diferentes estructuras de respuesta.
 *
 * Soporta:
 *
 * []
 *
 * {
 *   clients: []
 * }
 *
 * {
 *   results: []
 * }
 *
 * {
 *   data: []
 * }
 */
const normalizeClients = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.clients)) {
    return data.clients;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

// =========================================================
// ERROR API
// =========================================================

const getApiErrorMessage = (
  data,
  fallback = "Ocurrió un error en el servidor."
) => {
  if (!data) {
    return fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  if (typeof data?.error === "string") {
    return data.error;
  }

  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.detail === "string") {
    return data.detail;
  }

  // Django REST Framework puede devolver:
  //
  // {
  //   "name": ["Este campo es obligatorio."]
  // }

  if (typeof data === "object") {
    const messages = [];

    Object.entries(data).forEach(
      ([field, value]) => {
        if (Array.isArray(value)) {
          value.forEach((message) => {
            messages.push(
              `${field}: ${message}`
            );
          });
        } else if (
          typeof value === "string"
        ) {
          messages.push(
            `${field}: ${value}`
          );
        }
      }
    );

    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  return fallback;
};

// =========================================================
// PHONE
// =========================================================

const cleanPhone = (phone) => {
  return String(phone || "").replace(
    /\D/g,
    ""
  );
};

// =========================================================
// COMPONENT
// =========================================================

export default function ClientsManager() {
  // =======================================================
  // STATES
  // =======================================================

  const [clients, setClients] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [error, setError] =
    useState("");

  const [form, setForm] =
    useState({
      ...INITIAL_FORM,
    });

  // =======================================================
  // LOAD CLIENTS
  // =======================================================

  const loadClients = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        CLIENTS_ENDPOINT,
        {
          method: "GET",

          headers: {
            Accept:
              "application/json",
          },

          credentials: "include",
        }
      );

      let data = null;

      try {
        data =
          await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          getApiErrorMessage(
            data,
            `Error cargando huéspedes: HTTP ${response.status}`
          )
        );
      }

      const list =
        normalizeClients(data);

      setClients(list);
    } catch (err) {
      console.error(
        "CLIENTS LOAD ERROR:",
        err
      );

      setError(
        err?.message ||
          "No fue posible cargar los huéspedes."
      );

      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadClients();
  }, []);

  // =======================================================
  // FORM CHANGE
  // =======================================================

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // =======================================================
  // ADD CLIENT
  // =======================================================

  const addClient = async () => {
    const name =
      form.name.trim();

    const phone =
      form.phone.trim();

    const message =
      form.message.trim();

    // =====================================================
    // VALIDATION
    // =====================================================

    if (!name) {
      setError(
        "El nombre del huésped es obligatorio."
      );

      document
        .getElementById(
          "client-name"
        )
        ?.focus();

      return;
    }

    if (!phone) {
      setError(
        "El teléfono o WhatsApp es obligatorio."
      );

      document
        .getElementById(
          "client-phone"
        )
        ?.focus();

      return;
    }

    try {
      setSaving(true);
      setError("");

      const response = await fetch(
        CLIENTS_ENDPOINT,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json",
          },

          credentials: "include",

          body: JSON.stringify({
            name,
            phone,
            message,
          }),
        }
      );

      let data = null;

      try {
        data =
          await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          getApiErrorMessage(
            data,
            `Error creando huésped: HTTP ${response.status}`
          )
        );
      }

      // ===================================================
      // RESET FORM
      // ===================================================

      setForm({
        ...INITIAL_FORM,
      });

      // ===================================================
      // RELOAD
      // ===================================================

      await loadClients();

      alert(
        "✓ Huésped registrado correctamente."
      );
    } catch (err) {
      console.error(
        "CLIENT CREATE ERROR:",
        err
      );

      setError(
        err?.message ||
          "No fue posible registrar el huésped."
      );
    } finally {
      setSaving(false);
    }
  };

  // =======================================================
  // SEARCH
  // =======================================================

  const filteredClients =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      if (!query) {
        return clients;
      }

      return clients.filter(
        (client) => {
          const name =
            String(
              client?.name || ""
            ).toLowerCase();

          const phone =
            String(
              client?.phone || ""
            ).toLowerCase();

          const message =
            String(
              client?.message || ""
            ).toLowerCase();

          return (
            name.includes(query) ||
            phone.includes(query) ||
            message.includes(query)
          );
        }
      );
    }, [
      clients,
      search,
    ]);

  // =======================================================
  // WHATSAPP
  // =======================================================

  const openWhatsApp = (
    phone,
    message = ""
  ) => {
    const number =
      cleanPhone(phone);

    if (!number) {
      return;
    }

    const encodedMessage =
      message
        ? `?text=${encodeURIComponent(
            message
          )}`
        : "";

    const whatsappUrl =
      `https://wa.me/${number}${encodedMessage}`;

    window.open(
      whatsappUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =======================================================
  // FORMAT DATE
  // =======================================================

  const formatDate = (
    value
  ) => {
    if (!value) {
      return "—";
    }

    try {
      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return "—";
      }

      return new Intl.DateTimeFormat(
        "es-CO",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      ).format(date);
    } catch {
      return "—";
    }
  };

  // =======================================================
  // TOTAL WITH PHONE
  // =======================================================

  const clientsWithPhone =
    useMemo(() => {
      return clients.filter(
        (client) =>
          Boolean(
            cleanPhone(
              client?.phone
            )
          )
      ).length;
    }, [clients]);

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <section className="clients">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="clients__header">

        <div className="clients__heading">

          <span className="clients__eyebrow">
            ORION BEACH HOTEL · KITE HOUSE
          </span>

          <h1 className="clients__title">
            Huéspedes
          </h1>

          <p className="clients__subtitle">
            Gestiona contactos,
            consultas y oportunidades
            de reserva desde un solo lugar.
          </p>

        </div>

        <div className="clients__header-actions">

          <div className="clients__status">

            <span className="clients__status-dot" />

            {import.meta.env.DEV
              ? "Modo desarrollo"
              : "Sistema en producción"}

          </div>

          <button
            type="button"
            className="clients__refresh"
            onClick={loadClients}
            disabled={loading}
          >

            <span>
              ↻
            </span>

            {loading
              ? "Actualizando..."
              : "Actualizar"}

          </button>

        </div>

      </header>


      {/* =================================================
          STATS
      ================================================= */}

      <div className="clients__stats">

        {/* TOTAL */}

        <div className="clients__stat">

          <div className="clients__stat-icon">
            👥
          </div>

          <div>

            <span>
              Huéspedes registrados
            </span>

            <strong>
              {clients.length}
            </strong>

          </div>

        </div>


        {/* SEARCH */}

        <div className="clients__stat">

          <div className="clients__stat-icon">
            🔎
          </div>

          <div>

            <span>
              Resultados actuales
            </span>

            <strong>
              {filteredClients.length}
            </strong>

          </div>

        </div>


        {/* WHATSAPP */}

        <div className="clients__stat">

          <div className="clients__stat-icon">
            💬
          </div>

          <div>

            <span>
              Contactables
            </span>

            <strong>
              {clientsWithPhone}
            </strong>

          </div>

        </div>

      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (
        <div className="clients__error">

          <div className="clients__error-icon">
            !
          </div>

          <div>

            <strong>
              Atención
            </strong>

            <p>
              {error}
            </p>

          </div>

          <button
            type="button"
            onClick={() =>
              setError("")
            }
          >
            Cerrar
          </button>

        </div>
      )}


      {/* =================================================
          NEW CLIENT
      ================================================= */}

      <div className="clients__card">

        <div className="clients__card-header">

          <div>

            <span className="clients__card-eyebrow">
              NUEVO CONTACTO
            </span>

            <h2>
              Registrar huésped
            </h2>

            <p>
              Añade rápidamente una persona
              interesada en hospedarse en
              Orión Beach Hotel.
            </p>

          </div>

          <div className="clients__card-symbol">
            +
          </div>

        </div>


        {/* =================================================
            FORM
        ================================================= */}

        <div className="clients__form">

          {/* NAME */}

          <div className="clients__field">

            <label htmlFor="client-name">
              Nombre completo
            </label>

            <div className="clients__input-wrapper">

              <span className="clients__input-icon">
                👤
              </span>

              <input
                id="client-name"
                name="name"
                type="text"
                autoComplete="name"
                placeholder="Ej. Juan Pérez"
                value={form.name}
                onChange={
                  handleChange
                }
                className="clients__input"
              />

            </div>

          </div>


          {/* PHONE */}

          <div className="clients__field">

            <label htmlFor="client-phone">
              WhatsApp / Teléfono
            </label>

            <div className="clients__input-wrapper">

              <span className="clients__input-icon">
                📱
              </span>

              <input
                id="client-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                placeholder="+57 300 000 0000"
                value={form.phone}
                onChange={
                  handleChange
                }
                className="clients__input"
              />

            </div>

          </div>


          {/* MESSAGE */}

          <div className="clients__field clients__field--full">

            <label htmlFor="client-message">
              Consulta del huésped
            </label>

            <div className="clients__textarea-wrapper">

              <span className="clients__textarea-icon">
                💬
              </span>

              <textarea
                id="client-message"
                name="message"
                rows="4"
                placeholder="Ej. Desea información sobre habitaciones, fechas, kitesurf o disponibilidad..."
                value={form.message}
                onChange={
                  handleChange
                }
                className="clients__textarea"
              />

            </div>

          </div>


          {/* ACTIONS */}

          <div className="clients__form-actions">

            <button
              type="button"
              className="clients__button clients__button--secondary"
              onClick={() =>
                setForm({
                  ...INITIAL_FORM,
                })
              }
              disabled={saving}
            >
              Limpiar
            </button>

            <button
              type="button"
              className="clients__button"
              onClick={addClient}
              disabled={saving}
            >

              {saving ? (
                <>
                  <span className="clients__spinner" />

                  Guardando...
                </>
              ) : (
                <>
                  <span>
                    +
                  </span>

                  Registrar huésped
                </>
              )}

            </button>

          </div>

        </div>

      </div>


      {/* =================================================
          CLIENT LIST
      ================================================= */}

      <div className="clients__card clients__card--list">

        <div className="clients__list-header">

          <div>

            <span className="clients__card-eyebrow">
              BASE DE CONTACTOS
            </span>

            <h2>
              Huéspedes registrados
            </h2>

            <p>
              Consulta y contacta rápidamente
              a tus huéspedes.
            </p>

          </div>


          {/* SEARCH */}

          <div className="clients__search">

            <span>
              🔎
            </span>

            <input
              type="search"
              placeholder="Buscar huésped..."
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
            />

            {search && (
              <button
                type="button"
                onClick={() =>
                  setSearch("")
                }
                aria-label="Limpiar búsqueda"
              >
                ×
              </button>
            )}

          </div>

        </div>


        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (

          <div className="clients__loading">

            <div className="clients__spinner clients__spinner--large" />

            <p>
              Cargando huéspedes...
            </p>

          </div>

        ) : filteredClients.length === 0 ? (

          /* ===============================================
             EMPTY
          =============================================== */

          <div className="clients__empty">

            <div className="clients__empty-icon">
              🏝️
            </div>

            <h3>
              {search
                ? "No encontramos resultados"
                : "Todavía no hay huéspedes"}
            </h3>

            <p>
              {search
                ? "Prueba con otro nombre, teléfono o consulta."
                : "Los huéspedes registrados aparecerán automáticamente aquí."}
            </p>

            {!search && (
              <button
                type="button"
                className="clients__empty-button"
                onClick={() =>
                  document
                    .getElementById(
                      "client-name"
                    )
                    ?.focus()
                }
              >
                + Registrar primer huésped
              </button>
            )}

          </div>

        ) : (

          /* ===============================================
             TABLE
          =============================================== */

          <div className="clients__table-wrapper">

            <table className="clients__table">

              <thead>

                <tr>

                  <th>
                    Huésped
                  </th>

                  <th>
                    Contacto
                  </th>

                  <th>
                    Consulta
                  </th>

                  <th>
                    Registro
                  </th>

                  <th>
                    Acción
                  </th>

                </tr>

              </thead>

              <tbody>

                {filteredClients.map(
                  (
                    client,
                    index
                  ) => {

                    const phone =
                      client?.phone;

                    const name =
                      client?.name ||
                      "Sin nombre";

                    const initial =
                      name
                        .charAt(0)
                        .toUpperCase();

                    return (
                      <tr
                        key={
                          client?.id ||
                          `client-${index}`
                        }
                      >

                        {/* =================================
                            NAME
                        ================================= */}

                        <td>

                          <div className="clients__person">

                            <div className="clients__avatar">

                              {initial}

                            </div>

                            <div>

                              <strong>
                                {name}
                              </strong>

                              <span>
                                Huésped
                              </span>

                            </div>

                          </div>

                        </td>


                        {/* =================================
                            PHONE
                        ================================= */}

                        <td>

                          <div className="clients__phone">

                            <span>
                              {phone ||
                                "Sin teléfono"}
                            </span>

                            {phone && (
                              <button
                                type="button"
                                className="clients__whatsapp"
                                onClick={() =>
                                  openWhatsApp(
                                    phone
                                  )
                                }
                              >
                                WhatsApp
                              </button>
                            )}

                          </div>

                        </td>


                        {/* =================================
                            MESSAGE
                        ================================= */}

                        <td>

                          <div className="clients__message">

                            {client?.message ||
                              "Sin consulta registrada"}

                          </div>

                        </td>


                        {/* =================================
                            DATE
                        ================================= */}

                        <td>

                          <span className="clients__date">

                            {formatDate(
                              client?.created_at ||
                                client?.createdAt ||
                                client?.date
                            )}

                          </span>

                        </td>


                        {/* =================================
                            ACTION
                        ================================= */}

                        <td>

                          {phone ? (
                            <button
                              type="button"
                              className="clients__action"
                              onClick={() =>
                                openWhatsApp(
                                  phone,
                                  `Hola ${name}, te escribimos desde Orión Beach Hotel. ¿En qué podemos ayudarte con tu reserva?`
                                )
                              }
                            >
                              Contactar

                              <span>
                                →
                              </span>

                            </button>
                          ) : (
                            <span className="clients__no-action">
                              —
                            </span>
                          )}

                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>

        )}

      </div>


      {/* =================================================
          FOOTER
      ================================================= */}

      <footer className="clients__footer">

        <span>
          ORION BEACH HOTEL
        </span>

        <i />

        <span>
          KITE HOUSE
        </span>

        <i />

        <span>
          LA GUAJIRA · COLOMBIA
        </span>

      </footer>

    </section>
  );
}